import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { SAMPLES_CONFIG } from '@df/samples/config/samples.config';
import { SamplesBasket } from '@df/samples/samples-basket';
import { NSamples } from '@df/samples/samples.namespace';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';
import compact from 'lodash-es/compact';
import difference from 'lodash-es/difference';
import find from 'lodash-es/find';
import take from 'lodash-es/take';
import union from 'lodash-es/union';
import without from 'lodash-es/without';
import { Subject } from 'rxjs';
import { Product } from '../../catalog/product/product.decorator';
import { Sample } from './sample';
import { SampleFactory } from './sample.factory';
@Injectable({ providedIn: 'root' })
export class Samples {
    updateSubject = new Subject<void>();
    status = new Status();
    private _items: Sample[] = [];
    private _data: number[] = [];
    constructor(private SampleFactory: SampleFactory, private samplesBasket: SamplesBasket, private customer: Customer, private customerModel: CustomerModel, private localStorageService: LocalStorageService, private requestService: RequestService, private router: Router,
    @Inject(SAMPLES_CONFIG)
    private configuration: NSamples.IConfig) {
        this.register();
        this.onInit();
        this.samplesBasket.basketUpdateSubject.subscribe(() => this.onSamplesBasketUpdate());
        this.customerModel.obs.status.subscribe(() => {
            if (!this.customer.model.isLoggedIn) {
                this.reset();
            }
        });
    }
    get config(): NSamples.IConfig {
        return this.configuration;
    }
    get limit(): number | undefined {
        return this.config.limit;
    }
    private get storageName(): string {
        return 'customerSamples';
    }
    private onSamplesBasketUpdate(): void {
        if (this.samplesBasket && !this.samplesBasket.items?.length) {
            this.reset();
        }
    }
    protected onInit(): void {
        this.setData(this.localStorageService.get(this.storageName) || []);
    }
    reset(): void {
        this.setData([]);
    }
    /**
     * Data setter
     * Syncs data with local storage and items
     * @param  {number[]} data
     */
    setData(data: number[]) {
        let dataToSet = data.filter(i => i > 0);
        // app
        if (this.limit !== undefined) {
            dataToSet = take(dataToSet, this.limit);
        }
        this.localStorageService.set(this.storageName, dataToSet);
        this._data = dataToSet;
        // remove no longer existing
        const toRemove = this._items.filter(item => this._data.indexOf(item.productId) === -1);
        toRemove.forEach(item => item.onDestroy());
        this._items = difference(this._items, toRemove);
        // add new ones
        difference(this._data, this._items.map(i => i.productId)).forEach(productId => this._items.push(this.SampleFactory.create(productId)));
        this.next();
    }
    protected next() {
        this.updateSubject.next();
    }
    /**
     * Data getter
     * @return {number[]} [description]
     */
    get data(): number[] {
        return this._data;
    }
    /**
     * Items getter
     * @return {Sample[]}
     */
    get items(): Sample[] {
        return this._items;
    }
    /**
     * Adds Sample
     * @param  {Product} product
     * @param  {number} sampleId
     */
    addSample(product: Product<IProductDataV2>, sampleId: number): void {
        this.setData(union(this.data, [sampleId]));
        if (this.config.toWishlist) {
            this.customer.wishlist.add(undefined, { product: product.id });
        }
    }
    /**
     * Toggles sample
     * @param  {Product} product
     * @param  {number} sampleId
     */
    toggleSample(product: Product<IProductDataV2>, sampleId: number): void {
        if (this.isSampleAdded(sampleId)) {
            this.removeSample(sampleId);
        }
        else {
            this.addSample(product, sampleId);
        }
    }
    /**
     * Removes sample
     * @param  {number} sampleId
     */
    removeSample(sampleId: number): void {
        // WARNING: you have to set data when you change items!
        this.setData(without(this.data, sampleId));
    }
    /**
     * Gets sample item data
     * @param  {number} sampleId
     * @return {Sample}
     */
    getSample(sampleId: number): Sample | undefined {
        const id = sampleId;
        return find(this._items, item => item.productId === id);
    }
    /**
     * Checks if sample is added
     * @param  {number} sampleId
     * @return {boolean}
     */
    isSampleAdded(sampleId: number): boolean {
        return !!this.getSample(sampleId);
    }
    /**
     * Sends submission request
     * Adds samples to sample quote
     * Then redirects to samples checkout
     */
    submit(): Promise<any> {
        return this.createQuote(this.status, () => {
            if (this.config.checkoutUrl) {
                this.router.navigate([this.config.checkoutUrl]);
            }
        });
    }
    /**
     * Sends request to BE
     */
    createQuote(status?: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback): Promise<any> {
        return this.requestService.send(status, {
            path: NSamples.ENDPOINTS.createQuote,
            data: this.getSubmissionData()
        }, onSuccess, onError);
    }
    /**
     * Prepares data for submission
     * @return {IAddToBasketRequestData}
     */
    private getSubmissionData(): NSamples.ISamplesAddToBasketRequestData {
        return {
            products: compact(this._items.map(item => item.toBasketRequest())),
            sampleQuote: true
        };
    }
    private register() {
        this.customer.register({
            id: 'samples',
            listener: 'customersamples',
            account: false
        }, this);
    }
}
